import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { Selectors } from "../../Everou/_reducers/app.reducer";
import { auditLogsActions } from "../../Everou/_actions/auditLogs.actions";
import { AuditLog } from "../../Everou/_models/AuditLog";
import { AUDIT_LOG_CODES } from "../../Everou/_constants";
import { TimeHelper } from "../../Everou/_helpers";
import { AuditLogsHelper } from "../../Everou/_helpers/AuditLogsHelper";
import { GenericLogsPage } from "../../Everou/_components/GenericLogsPage";
import { AuditDetails } from "../../Everou/AuditLogsModule/AuditDetails";
import { Icons } from "../../Common/_assets/icons/Icons";

export function PMSLocationAuditLogs() {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //GLOBAL STATE
    const logs              = useSelector(Selectors.getAuditLogs);
    const { begin, end }    = useSelector(Selectors.getAuditLogsDateFilter);
    const timezone          = useSelector(Selectors.getPMSLocationSelectedTimezone);
    const locationUid       = useSelector(Selectors.getPMSLocationSelectedUid);
    const devices           = useSelector(Selectors.getPMSLocationDevices);
    const isRequesting      = useSelector(Selectors.getAuditLogsIsRequesting);
    const isRequestingFirst = useSelector(Selectors.getAuditLogsIsRequestingFirst);
    const nextPage          = useSelector(Selectors.getAuditLogsNextPage);
    const selectedUid       = useSelector(Selectors.getAuditLogsSelectedUid);

    //LOCAL STATE
    const isMounting = useRef(true);

    //ACTION HANDLERS
    const handleRefreshLogs = () => dispatch(auditLogsActions.getAuditFirstPage(locationUid));
    const handleNextPage    = () => dispatch(auditLogsActions.getAuditNextPage(nextPage, isRequesting, locationUid));
    const handleLogsDateStartChange = date =>
        dispatch(auditLogsActions.setDateFilter({ begin: date }))
    ;
    const handleLogsDateEndChange = date =>
        dispatch(auditLogsActions.setDateFilter({ end: date }))
    ;
    const handleSelectLog = uid => dispatch(auditLogsActions.selectLog(uid));
    const handleGoBack     = () => dispatch(auditLogsActions.selectLog(null));

    //INITIALIZATION
    useEffect(() => {
        dispatch(auditLogsActions.selectLog(null));
        dispatch(auditLogsActions.resetFilters());
    }, [dispatch, locationUid]);

    useEffect(() => {
        if (!timezone)
            return;

        dispatch(auditLogsActions.setDateFilter({
            begin: TimeHelper.localizedStartOfTodayIsoString(timezone),
            end: TimeHelper.localizedEndOfTodayIsoString(timezone),
        }));
    }, [dispatch, locationUid, timezone]);

    useEffect(() => () => {
        dispatch(auditLogsActions.selectLog(null));
    }, [dispatch]);

    useEffect(() => {
        if (isMounting.current) {
            isMounting.current = false;
            return;
        }

        if (!begin || !end)
            return;

        dispatch(auditLogsActions.getAuditFirstPage(locationUid));
    }, [dispatch, begin, end, locationUid]);

    //VIEW
    return (
        <div className="d-flex h-100 w-100 overflow-hidden">
            <div className="flex-fill overflow-hidden d-flex flex-column">
                <GenericLogsPage
                    content={table()}

                    isThereLogs={logs.length !== 0}
                    isRequesting={isRequesting}
                    requestingFirst={isRequestingFirst}
                    nextPage={nextPage}

                    startIsoString={begin}
                    endIsoString={end}

                    timezone={timezone}

                    handleNextPage={handleNextPage}
                    handleLogsDateStartChange={handleLogsDateStartChange}
                    handleLogsDateEndChange={handleLogsDateEndChange}
                    handleRefreshLogs={handleRefreshLogs}
                />
            </div>
            {detailPane()}
        </div>
    );

    function detailPane() {
        if (!selectedUid)
            return null;

        return (
            <div className="flex-shrink-0 d-flex overflow-auto pl-3">
                {goBackButton()}
                <AuditDetails
                    timezone={timezone}
                    devices={devices}
                />
            </div>
        );
    }

    function goBackButton() {
        return (
            <div
                onClick={handleGoBack}
                className="
                    flex-shrink-0
                    d-flex
                    align-items-center
                    c-hover-230
                    cp
                "
            >
                <div className="
                    mx-1

                    bg-white
                    rounded-circle
                    c-box-shadow-centered
                    c-hover-230
                ">
                    <img
                        className="c-img-2rem"
                        src={Icons.previousIcon}
                        alt="back icon"
                    />
                </div>
            </div>
        );
    }

    function table() {
        return (
            <table
                className={`
                    mb-0
                    table
                    c-line-height-1rem
                `}
            >
                <thead>
                    <tr>
                        {stickyThead(t("common.code"))}
                        {stickyThead(t("common.type"))}
                        {stickyThead(t("common.description"))}
                        {stickyThead(t("common.date"))}
                        {stickyThead(t("common.user"))}
                    </tr>
                </thead>
                <tbody>
                    {logs.map((log, index) => singleLog(log, index))}
                </tbody>
            </table>
        );

        function stickyThead(name) {
            return(
                <th className="
                    c-sticky-top
                    c-bg-grey-230
                    text-capitalize
                "
                    scope="col"
                >
                    {name}
                </th>
            );
        }

        function singleLog(log = AuditLog(), index) {
            return (
            <tr key={index}
                onClick={() => handleSelectLog(log.id)}
                className={`
                    ${selectedUid === log.id
                        ? "c-bg-primary-light-2"
                        : "c-hover-240"
                    }
                    c-cursor-default
                `}
            >
                <td className={`
                    ${log.code === AUDIT_LOG_CODES.ERROR
                        ? "text-danger"
                        : ""
                    }
                `}>
                    {log.code}
                </td>
                <td>{AuditLogsHelper.getTypeUiName(log.type)}</td>
                <td>{AuditLogsHelper.getAuditLogDescription(log.path, true, log.content)}</td>
                <td>{TimeHelper.localizeIsoStringToFormat(
                    log.datetime,
                    timezone,
                    TimeHelper.getTimeFormats().DATE,
                )}</td>
                <td className="text-break">{log.userEmail}</td>
            </tr>
            );
        }
    }
}
